// `shortlist-header-tooltip.tpl.html` — the menu behind the header's ⋮.
//
// ⚠️ WHAT IS HERE IS WHAT HAS SOMEWHERE TO GO, and the two that are missing are missing for
// different reasons:
//
//   - **Reports** is behind `ctrl.reportsEnabled`, which is `careero.groups[ADMIN]`. The route
//     IS served now (`dialog.tsx` renders `ReportsRoute` for `/chrome-extension/reports`), so
//     the caller passes the group and the item opens a real screen. The prop still defaults
//     to `false`: a non-admin sees the menu without the entry, as upstream shows them.
//   - **Settings** opens `extension-preferences-tooltip`, a second component with its own
//     template and its own user-preferences API. It is not ported, so the item is behind a
//     flag of its own rather than rendered inert.
//
// The product switch and Help both work, which is the difference between a menu and a button
// that opens an empty box.
import type { ReactElement } from 'react'
import { WooSwitch } from '../common/WooSwitch.tsx'

export interface ShortlistHeaderTooltipProps {
  /** `extensionProductDisplayNames[currentProduct]` — the switch's own label. */
  productName: string
  /** `ctrl.isProductToggleOn` — on means Candidates, off means Contacts. */
  candidatesSelected: boolean
  /** `ctrl.switchProduct(...)`. */
  onSwitchProduct?: (toCandidates: boolean) => void
  /** `ctrl.toggleContact()`. */
  onHelp?: () => void
  /** `ctrl.loggedIn` — every action below the switch is gated on it. */
  loggedIn?: boolean
  /** `ctrl.reportsEnabled` — see the note above. */
  reportsEnabled?: boolean
  onReports?: () => void
  /** The preferences tooltip is unported; `false` keeps the item off rather than inert. */
  settingsEnabled?: boolean
  onSettings?: () => void
  /** `$state.is(contactState)` — the Help item draws as active on the contact screen. */
  helpActive?: boolean
}

export function ShortlistHeaderTooltip({
  productName,
  candidatesSelected,
  onSwitchProduct,
  onHelp,
  loggedIn = true,
  reportsEnabled = false,
  onReports,
  settingsEnabled = false,
  onSettings,
  helpActive = false,
}: ShortlistHeaderTooltipProps): ReactElement {
  return (
    <div className="shortlist-header-tooltip-content-container">
      <div className="shortlist-header-tooltip-title-switch-container">
        <div className="shortlist-header-tooltip-content-title">Switch view</div>
        <div className="shortlist-header-switch-container">
          {/* ⚠️ THE PRODUCT NAME IS THE SWITCH'S TRANSCLUDED CONTENT, NOT ITS `label`, AND THE
              TWO LAND IN DIFFERENT ELEMENTS. `shortlist-header-tooltip.tpl.html:11` puts
              `{{extensionProductDisplayNames[currentProduct]}}` BETWEEN the `<woo-switch>`
              tags, and `switch.tpl.html` renders transclusion into
              `<span class="switch-inner-text">` — INSIDE `.switch-container`. `label` is a
              different slot: `<span class="switch-label">`, a SIBLING of the container.

              The note that stood here claimed `WooSwitch` renders the two "in the same place".
              It does not, and the difference is the whole control: beside build 354 the name
              sat outside the pill in orange with the pill stretched to fill the row, where
              prod has it inside a compact one. `switch-small` sizes the container, and a
              container with no content has nothing to size to.

              There is no `aria-label` now — `WooSwitch` derives that from `label`, which is
              absent here as it is upstream. `role="switch"` plus the inner text carries it. */}
          <WooSwitch
            className="switch-small"
            isOn={candidatesSelected}
            onToggle={() => {
              onSwitchProduct?.(!candidatesSelected)
            }}
          >
            {productName}
          </WooSwitch>
          {/* `shortlist-header-product-unavailable-container` — the "Activate Woo for
              contacts" CTA and its thank-you state — is NOT here. It appears only when the
              switch is thrown toward a product the tenant has not bought, which this port
              cannot know: `switchProduct` learns it from the failure of the state change it
              attempts. Rendering the CTA would mean guessing at entitlement. */}
        </div>
      </div>
      <div className="shortlist-header-tooltip-actions-container shortlist-header-tooltip-actions-container-padding-top">
        {loggedIn && reportsEnabled ? (
          <div
            className="shortlist-header-tooltip-action"
            role="button"
            tabIndex={0}
            onClick={onReports}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return
              event.preventDefault()
              onReports?.()
            }}
          >
            <span title="Reports">
              <i className="fas fa-chart-bar" />
              &nbsp;&nbsp;Reports
            </span>
          </div>
        ) : null}
        {loggedIn ? (
          <div
            className="shortlist-header-tooltip-action"
            role="button"
            tabIndex={0}
            onClick={onHelp}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return
              event.preventDefault()
              onHelp?.()
            }}
          >
            <span className={`contact-cta${helpActive ? ' active' : ''}`} title="Contact us">
              <i className="fas fa-question-circle" aria-hidden="true" />
              &nbsp;&nbsp;Help
            </span>
          </div>
        ) : null}
        {loggedIn && settingsEnabled ? (
          <div
            className="shortlist-header-tooltip-action"
            role="button"
            tabIndex={0}
            onClick={onSettings}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return
              event.preventDefault()
              onSettings?.()
            }}
          >
            <span className="user-preferences-cta" title="Settings">
              <i className="fas fa-cog" aria-hidden="true" />
              &nbsp;&nbsp;Settings
            </span>
          </div>
        ) : null}
      </div>
    </div>
  )
}
